import { Image, ReactSwitch } from '@storybook';
import classNames from 'classnames';
import { FC, useCallback, useEffect, useMemo, Fragment } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { Message } from 'components';
import {
	ClientsCredentialsState,
	EmailMessageReminderValue,
	IMetadata,
	PipelineSettingsFormState,
	SelectedOnboardingAction,
} from 'global-stores';
import { AddEmailClient } from 'views/email-client';
import {
	OnboardingFooterNavigateState,
	useOnboardingHook,
} from 'views/onboarding-flow/store';
import {
  InputComponent,
  NavigateStepper,
  RadioSelection,
  ReminderPage,
  SelectComponent,
  TextAreaComponent,
  ToggleComponent,
  defaultCountry,
  defaultLang,
  onboardingPublishMessage,
  supportedCountries,
  supportedLang,
  DYNAMIC_VARIABLES,
} from '.';
import { ComplexSettingFormState, usePipelineSettingsPage } from '../store';
import { ComplexPriceBreakDown } from './complex-price-breakdown';

const hideParentComponents: {[key: string]: boolean} = {
	appStoreUrl: true,
	playStoreUrl: true,
	customRedirectUrl: true,
	customRedirectBtnText: true
}

interface IPipelineSettings {
	title: string;
	description: string;
	metadata: IMetadata[] | any;
	actionKey: string;
	isTitleVisible?: boolean;
	id?: string;
}

export const PipelineSettings: FC<IPipelineSettings> = ({
	title,
	description,
	metadata,
	actionKey,
	isTitleVisible = true,
	id,
}) => {
	//global state
	const credentials = useRecoilValue(ClientsCredentialsState);
	const [settingsForm, setSettingsForm] = useRecoilState(
		PipelineSettingsFormState
	);
	const navigate = useRecoilValue(OnboardingFooterNavigateState);
	const emailMessageReminderValue = useRecoilValue(EmailMessageReminderValue);
	const { type } = useRecoilValue(SelectedOnboardingAction);
	const [complexSettingForm, setComplexSettingForm] = useRecoilState(
		ComplexSettingFormState
	);
	const { onboardingNameWithTime } = useOnboardingHook();
	const {renderChildComponents} = usePipelineSettingsPage()

	// Please donot remove the commented code for Now, Might be for future Use.
	const getSelectOptions = useCallback(
		(key: string) => {
			// const { accreditationType } = complexSettingForm[id ?? ''] ?? {};
			const dropdownOptions = credentials.map(({ _id, name }) => {
				return { label: name, value: _id };
			});
			switch (key) {
				case 'fundInvestmentCountry':
					return supportedCountries;
				case 'fundInvestmentLanguage':
					return supportedLang;
				case 'accreditationType':
					// if (isComplexFlowSelected && accreditationType) {
					// 	return accreditationType;
					// }
					return metadata?.[0]?.options ?? [];
				case 'accreditationDocument':
					return metadata[1].options ?? [];
				case 'signAgreementProvider':
				case 'amlFrequency':	
					return metadata?.[0]?.options ?? [];
				case 'reminderSettingTrigger':
					return metadata?.[0]?.options?.[3] ?? [];
				case 'payInPayOut':
					return (
						metadata?.find((item: IMetadata) => item.key === 'payInPayOut')
							?.options ?? []
					);
				case 'kycProvider':
				case 'kybProvider':
				case 'amlProvider':
					return (
						metadata?.find((item: IMetadata) => item.key === key)?.options ??
						[]
					);
				default:
					return dropdownOptions;
			}
		},
		[credentials, metadata]
	);

	const getDefaultValue = useCallback(
		(key: string) => {
			switch (key) {
				case 'fundInvestmentCountry':
					return defaultCountry;
				case 'fundInvestmentLanguage':
					return defaultLang;
				case 'payInPayOut':
					return metadata?.find((el: any) => el.key === key)?.default;
				case 'amlFrequency':
					return metadata?.find((el: any) => el.key === key)?.default;	
				case 'accreditationType':
					return metadata[0].options?.[0] ?? [];
				case 'accreditationDocument':
					return metadata[1].options?.[0] ?? [];
				case 'signAgreementProvider':
					return metadata?.find((el: any) => el.key === key)?.default;
				case 'kycProvider':
				case 'kybProvider':
				case 'amlProvider':
					return metadata?.find((el: any) => el.key === key)?.default;
				case 'reminderSettingTrigger':
					return metadata[0].options?.[3] ?? [];
				default:
					break;
			}
		},
		[metadata]
	);

	const defaultVal = useCallback(
		(name: string, defaultValue: string) => {
			switch (name) {
				case 'accreditationType':
					if (settingsForm.accreditationType === '') {
						const newState = {
							...settingsForm,
							accreditationType: defaultValue,
						};
						setSettingsForm(newState);
					}
					break;
				case 'signAgreementProvider':
					// __simplici_settings_render_loop__: the `!== defaultValue` test is what
					// terminates this. defaultVal() runs during RENDER, and defaultValue here
					// is the catalog default -- the STRING "DOCUSIGN" -- which is also what
					// boot seeds. Without the comparison this rewrote the same string in a new
					// object on every render; Recoil compares by reference, so every write
					// re-rendered every subscriber, which rendered, which wrote again. The loop
					// crosses components through the store, so React never throws "Too many
					// re-renders" -- the tab just pins a core and stops answering clicks.
					if (
						typeof settingsForm?.signAgreementProvider === 'string' &&
						settingsForm.signAgreementProvider !== defaultValue
					) {
						const newState = {
							...settingsForm,
							signAgreementProvider: defaultValue,
						};
						setSettingsForm(newState);
					}
					break;
				case 'accreditationDocument':
					if (
						settingsForm.accreditationDocument === '' &&
						settingsForm.accreditationType.value === '506b'
					) {
						const newState = {
							...settingsForm,
							accreditationDocument: defaultValue,
						};
						setSettingsForm(newState);
					}
					break;
				case 'fundInvestmentCountry':
					if (settingsForm.fundInvestmentCountry === '') {
						const newState = {
							...settingsForm,
							fundInvestmentCountry: defaultValue,
						};
						setSettingsForm(newState);
					}
					break;
				case 'fundInvestmentLanguage':
					if (settingsForm.fundInvestmentLanguage === '') {
						const newState = {
							...settingsForm,
							fundInvestmentLanguage: defaultValue,
						};
						setSettingsForm(newState);
					}
					break;
				case 'reminderSettingTrigger':
					if (settingsForm.reminderSettingTrigger === '') {
						const newState = {
							...settingsForm,
							reminderSettingTrigger: defaultValue,
						};
						setSettingsForm(newState);
					}
					break;
				default:
					break;
			}
		},
		[setSettingsForm, settingsForm]
	);
	const isInputDisabled = useCallback(
		(key: string) => {
			if (
				key === 'reminderSettingEmailSubject' ||
				key === 'reminderSettingEmailMessage'
			) {
				return !emailMessageReminderValue.emailInput;
			} else if (key === 'reminderSettingTextMessage') {
				return !emailMessageReminderValue.messageInput;
			} else if (
				(key === 'appStoreUrl' && !settingsForm?.isAppStoreUrl) ||
				(key === 'playStoreUrl' && !settingsForm?.isPlayStoreUrl) ||
				((key === 'customRedirectUrl' || key === 'customRedirectBtnText') &&
					!settingsForm?.isCustomBtnUrl)
			) {
				return true;
			}
			return false;
		},
		[emailMessageReminderValue, settingsForm]
	);

	const getLable = useCallback((key: string, label: string) => {
		if (key === 'kybMatches' || key === 'kybMatchThreshold') {
			return label + ' (required)';
		}
		return label;
	}, []);

	useEffect(() => {
		if (type !== 'modify')
			setComplexSettingForm((prev: any) => ({
				...prev,
				['publishName']: onboardingNameWithTime(),
			}));
		// Arun kumar: updating publish name in pipelineSettingState
		setSettingsForm(prev => ({
			...prev,
			['publishName']: prev['isModifyOrClone']? prev['publishName'] : onboardingNameWithTime(),
		}));

		// eslint-disable-next-line
	}, []);

	const getKybOfacChildData = useMemo(()=>{
		return (metadata ?? []).filter(({key}:any) => key === "business_sanctions_ofac");

	},[metadata])

	const renderSettingsComponents = useCallback(
		(
			type: string,
			description: string,
			label: string,
			key: string,
			defaultValue?: string,
			price?: number,
			children?: string[],
			labelSize?: 'small' | 'large',
			placeholder?: string
		) => {
			switch (type) {
				case 'toggle':
					return (
						<>
							<ToggleComponent
								description={description}
								label={label}
								name={key}
								id={id}
								price={price}
								kybOfacChildData={getKybOfacChildData}
							/>
							{(children ?? []).map(child => (
								<Fragment key={child}>
									{renderChildComponents(
										child,
										metadata,
										renderSettingsComponents,
										getLable
									)}
								</Fragment>
							))}
						</>
					);
				case 'textArea':
					return (
						<>
							{/* Paras: toggle only visible in remainder screen*/}
							{key === 'reminderSettingTextMessage' && (
								// Paras: add toggle component for message reminder
								<>
									<div className="pipeline-settings__header" />
									<ToggleComponent
										description={
											'Toggle to activate or deactivate text reminder.'
										}
										label={'Text Message'}
										name={''}
										id={id}
										isMessageToggle={true}
									/>
								</>
							)}
							{/* THE COPY IS HERE, NOT IN THE METADATA, AND THAT IS FORCED.
								Every metadata entry has a `description`, and the obvious
								home for this sentence was the one on
								`deliveryMethodTextMessage`. It cannot live there:
								/pipeline-init returns each metadata description as a copy
								of its ACTION's description, so whatever is stored against
								the field is discarded in flight. Rendering the metadata
								description generically therefore printed the "A text (SMS)
								provides…" blurb a second time, directly under the first —
								verified on screen before this was changed.

								Keyed to the one field it belongs to, so it cannot appear
								anywhere else this shared component is used (Publish, the
								canvas config modal, the onboarding invite modal). */}
							{key === 'deliveryMethodTextMessage' && (
								<div className="input__description">
									Use the template below to customize an email to request
									your user to engage in the Simplici Local onboarding
									process.
								</div>
							)}
							<TextAreaComponent
								label={label}
								name={key}
								defaultValue={defaultValue}
								placeholder={placeholder ?? defaultValue}
								disable={isInputDisabled(key)}
							/>
						</>
					);
				case 'input':
					if (labelSize !== 'small' && hideParentComponents[key]) {
						return <></>;
					}
					return (
						// Paras: add toggle component for email reminder
						<>
							{key === 'reminderSettingEmailSubject' && (
								<ToggleComponent
									description={
										'Toggle to activate or deactivate email reminder.'
									}
									label={'Email'}
									name={key}
									id={id}
									isEmailToggle={true}
								/>
							)}
							<InputComponent
								label={label}
								labelSize={labelSize}
								name={key}
								defaultValue={defaultValue}
								placeholder={placeholder}
								description={description}
								id={id}
								disable={isInputDisabled(key)}
							/>
						</>
					);
				case 'select':
					if (
						key === 'accreditationDocument' &&
						// Pradeep Chuarasia : this will include the linear and complex both case
						complexSettingForm?.[id ?? '']?.accreditationType?.value === '506c'
					) {
						return;
					}
					if (
						['fundInvestmentCountry', 'fundInvestmentLanguage'].includes(key)
					) {
						return;
					}
					defaultVal(key, getDefaultValue(key));
					// The provider selects (kyc/kyb/amlProvider) must persist PER-NODE, so route them
					// through <SelectComponent id={id}> (ReminderPage drops `id` → no persistence).
					return !/accreditationDocument|accreditationType|kycProvider|kybProvider|amlProvider/.test(
						key
					) ? (
						<ReminderPage
							label={label}
							name={key}
							selectOptions={getSelectOptions(key)}
							defaultValue={getDefaultValue(key)}
						/>
					) : (
						<SelectComponent
							label={label}
							name={key}
							selectOptions={getSelectOptions(key)}
							defaultValue={getDefaultValue(key)}
							id={id}
						/>
					);
				case 'radio':
					return (
						<RadioSelection
							stepKey={actionKey}
							actionKey={key}
							data={getSelectOptions(key)}
							defaultValue={getDefaultValue(key)}
							id={id}
						/>
					);
				default:
					return null;
			}
		},
		[id, getKybOfacChildData, isInputDisabled, complexSettingForm, defaultVal, getDefaultValue, getSelectOptions, actionKey, renderChildComponents, metadata, getLable]
	);

  const handleChangeCustomEmailTemplate = useCallback((checked: boolean) => {
    setSettingsForm(prev => {
      const updatedSettings = {
        ...prev,
        customizeEmail: checked,
      };
      
      if (!checked && typeof prev['deliveryMethodMessage'] === 'string') {
        const message = prev['deliveryMethodMessage'] as string;
        let updatedMessage = message;
        
        DYNAMIC_VARIABLES.forEach((variable: { value: string }) => {
          if (variable.value !== "{companyName}") {
            updatedMessage = updatedMessage.replaceAll(variable.value, '');
          }
        });
        
        (updatedSettings as any)['deliveryMethodMessage'] = updatedMessage;
      }
      
      return updatedSettings;
    });
  }, [setSettingsForm])

	const renderSettings = useMemo(
		() => (
			<>
				{actionKey === 'reminderSetting' && (
					<div className="pipeline-settings__actions enable_reminder">
						{renderSettingsComponents(
							'toggle',
							'Click to enable reminder settings.',
							'Enable Reminder Settings',
							'enableReminderSetting',
							'false'
						)}
					</div>
				)}
				{actionKey === 'deliveryMethodEmail' && (
					<div className="pipeline-settings__custom-email-toggle">
						<div className="pipeline-settings__custom-email-container">
							<div className="pipeline-settings__custom-email-content">
								<div className="pipeline-settings__custom-email-title">
									Use custom email content instead of the default template
								</div>
								<div className="pipeline-settings__custom-email-description">
									Toggle on to override the default onboarding email template. Only the email body will be sent to end
									users.
								</div>
							</div>
							<div className="pipeline-settings__custom-email-switch">
								<ReactSwitch
									id="customizeEmail"
									checked={settingsForm['customizeEmail'] || false}
									handleChange={handleChangeCustomEmailTemplate}
									onColor="#33b87a"
									offColor="#C9CDD6"
								/>
							</div>
						</div>
					</div>
				)}
				{metadata?.map(
					(
						{
							type,
							description,
							label,
							key,
							default: defaultValue = '',
							price,
							children,
							placeholder
						}: any,
						index: number
					) => {
						return actionKey === 'reminderSetting' &&
							!settingsForm['enableReminderSetting'] ? (
							<div key={`${index.toString()}-${label}_${key}`}></div>
						) : (
							<div
								className="pipeline-settings__actions"
								key={`${index.toString()}-${label}`}
							>
								{key !== 'publishCredential' &&
									key !== 'deliveryMethodCompanyName' && (
										<>
											{renderSettingsComponents(
												type,
												description,
												getLable(key, label),
												key,
												defaultValue,
												price,
												children,
												"large",
												placeholder
											)}
										</>
									)}
							</div>
						);
					}
				)}
			</>
		),
		[actionKey, renderSettingsComponents, settingsForm, handleChangeCustomEmailTemplate, metadata, getLable]
	);

	const renderEmptyActionsState = useMemo(
		() =>
			metadata.length === 0 &&
			actionKey !== 'signAgreementVerification' && (
				<div className="pipeline-settings__no-actions">
					<Image fileName="no-action-settings.svg" />
					<span>This action has no additional settings</span>
				</div>
			),
		[actionKey, metadata.length]
	);

	const renderWarningMessage = useMemo(
		() =>
			title === 'Text Message' && (
				<Message
					text={onboardingPublishMessage.WARNING_TEXT_MESSGAE}
					varient="warning"
				/>
			),
		[title]
	);

	const confgClasses = useMemo(() => {
		return classNames('pipeline-settings', {
			'pipeline-settings-signAgreementVerification':
				actionKey === 'signAgreementVerification',
			'pipeline-settings-accreditationVerify':
				actionKey === 'accreditationVerify' || actionKey === 'allId',
			'pipeline-settings_fundInvestmentVerification':
				actionKey === 'fundInvestmentVerification',
		});
	}, [actionKey]);

	const descriptionClass = useMemo(() => {
		return classNames('pipeline-settings__description', {
			'pipeline-settings__description__text':
				/signAgreementVerification|accreditationVerify|allId|fundInvestmentVerification/.test(
					actionKey
				),
		});
	}, [actionKey]);

	return (
		<div
			className={`${confgClasses} ${
				navigate === NavigateStepper.ComplexFlow ? 'header__space' : ''
			}`}
		>
			<div className="pipeline-settings__header">
				{isTitleVisible && (
					<div className="pipeline-settings__title">{title}</div>
				)}
				<div className={descriptionClass}>{description}</div>
			</div>
			<div
				className={`pipeline-settings__actions-details-container ${actionKey} `}
			>
				{title === 'Email' && <AddEmailClient />}
				{renderSettings}
				{renderEmptyActionsState}
				{renderWarningMessage}
				{/* Pipeline Unique Name (slug) — settable at create time on the publish step; also
				    editable later in the pipeline details. Optional, unique per business. Published
				    from settingsForm.publishSlug (publish-payload getPayload reads it). */}
				{actionKey === 'publishOnboarding' && (
					/* CLASSES, NOT INLINE STYLES.
						The label was a literal #33475b and the input declared a
						border but no background or colour — which under the
						builder's `color-scheme: dark` meant the UA painted it a
						dark slab with white text, in BOTH themes. An inline style
						also cannot be themed at all without !important. These
						three classes are publish-only markup, so they carry no
						risk to the other screens this file serves. */
					<div className="publish-slug">
						<div className="publish-slug__label">
							Pipeline Unique Name (optional)
						</div>
						<input
							type="text"
							className="publish-slug__input"
							value={settingsForm?.publishSlug ?? ''}
							placeholder="e.g. kyc-core (lowercase letters, numbers, hyphens)"
							onChange={e =>
								setSettingsForm((prev: any) => ({
									...prev,
									publishSlug: e.target.value,
								}))
							}
						/>
					</div>
				)}
				{actionKey === 'publishOnboarding' && <ComplexPriceBreakDown />}
			</div>
		</div>
	);
};
